import React, {
  useEffect,
  useState,
  useRef,
  createContext,
  useContext,
} from 'react';
import { useRouter } from 'next/router';
import parsePath from 'src/lib/parse-path';
import CloseIcon from 'plaid-threads/Icons/CloseS1';

// components
import { Link } from 'src/components/Inputs';
import { Alert } from 'src/components/Feedback';

// helpers
import {
  useUserLocale,
  useForeignEquivalentPage,
  useDeviceSize,
} from 'src/hooks';
import { usePageLocale } from 'src/contexts';
import { useTrack } from 'src/contexts/AnalyticsContext';
import { Events } from 'src/lib/trackingEvents';
import {
  NEXT_PUBLIC_HAPPO_RUN,
  NEXT_PUBLIC_PERCY_RUN,
  NEXT_PUBLIC_IS_PREVIEW,
} from 'src/env';

// styles
import styles from './ForeignContentNotification.module.scss';
import { useMainTemplateContext } from 'src/contexts/MainTemplateContext/MainTemplateContext';

export const ForeignContentNotificationContext = createContext({
  isForeignContentNotificationOpen: false,
  setIsOpen: (isOpen: boolean) => {
    return null;
  },
});

export const ForeignContentNotificationProvider = ({ children }) => {
  const [isForeignContentNotificationOpen, setIsOpen] = useState(false);

  return (
    <ForeignContentNotificationContext.Provider
      value={{ isForeignContentNotificationOpen, setIsOpen }}
    >
      {children}
    </ForeignContentNotificationContext.Provider>
  );
};

export const useForeignContentNotification = () => {
  const context = useContext(ForeignContentNotificationContext);
  if (!context) {
    throw new Error(
      'useForeignContentNotification must be used within a ForeignContentNotificationProvider',
    );
  }
  return context;
};

// Sections that aren't localized — the banner must never show on these.
const UNLOCALIZED_ROUTES = [
  'blog',
  'docs',
  'partner-directory',
  'careers',
  'legal',
  'demo',
];

// Locale-shaped path segment (e.g. en, en-bg), used to skip a locale prefix.
const LOCALE_SEGMENT = /^[a-z]{2}(-[a-z]{2})?$/i;

const ForeignContentNotification: React.FC<{
  hasFloatingNav?: boolean;
}> = ({ hasFloatingNav = false }) => {
  const wrapper = useRef(null);
  const router = useRouter();
  const device = useDeviceSize();
  const pageLocale = usePageLocale();
  const { locale: userLocale } = useUserLocale();
  const { localizedEquivalent } = useForeignEquivalentPage();
  const { isForeignContentNotificationOpen, setIsOpen } =
    useForeignContentNotification();
  const { isForeignContentNotificationHidden } = useMainTemplateContext();
  const track = useTrack();
  const hasTrackedImpression = useRef(false);

  useEffect(() => {
    const parsedPath = parsePath(router.asPath);
    // Hide on unlocalized sections, skipping any leading locale segment
    // (e.g. /en-bg/blog). In this effect with the locale check so it re-runs
    // once the user locale resolves after mount — else the banner races open.
    const segments = parsedPath.pathname.split('/').filter(Boolean);
    const section = LOCALE_SEGMENT.test(segments[0] ?? '')
      ? segments[1]
      : segments[0];
    const isUnlocalizedRoute = UNLOCALIZED_ROUTES.includes(section);

    if (
      !userLocale?.localeCode ||
      NEXT_PUBLIC_PERCY_RUN ||
      NEXT_PUBLIC_HAPPO_RUN ||
      NEXT_PUBLIC_IS_PREVIEW ||
      isUnlocalizedRoute
    ) {
      setIsOpen(false);
    } else if (pageLocale !== userLocale?.localeCode) {
      setIsOpen(true);
    }

    const handleScroll = () => {
      const OFFSET_TOP = hasFloatingNav ? 0 : device.isMainNav ? 96 : 48;
      if (wrapper?.current?.style) {
        wrapper.current.style.top = OFFSET_TOP - window.scrollY + 'px';
      }
      if (wrapper?.current?.offsetTop <= 0) {
        wrapper.current.style.top = '0px';
      }
    };

    // NOTE: threads doesn't support inline styles or refs so we have to hack the animation like so:
    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [
    device,
    pageLocale,
    userLocale,
    hasFloatingNav,
    setIsOpen,
    router.asPath,
  ]);

  useEffect(() => {
    const isBannerVisible =
      isForeignContentNotificationOpen && !isForeignContentNotificationHidden;

    if (isBannerVisible && !hasTrackedImpression.current) {
      track({
        type: Events.VIEWED,
        payload: {
          trackingId: 'FOREIGN_CONTENT_NOTIFICATION_VIEWED',
          localeCode: userLocale?.localeCode ?? null,
          countryRegionName: userLocale?.countryRegionName ?? null,
          intrinsicLocale: userLocale?.intrinsicLocale ?? null,
          pageLocale,
        },
      });
      hasTrackedImpression.current = true;
    }

    // Reset the guard when the banner is dismissed/hidden so a future show re-tracks.
    if (!isBannerVisible) {
      hasTrackedImpression.current = false;
    }
  }, [
    isForeignContentNotificationOpen,
    isForeignContentNotificationHidden,
    track,
    userLocale,
    pageLocale,
  ]);

  if (isForeignContentNotificationOpen && !isForeignContentNotificationHidden) {
    return (
      <div className={styles.wrapper} ref={wrapper}>
        <Alert
          onClick={() => {
            setIsOpen(false);
          }}
          icon={<CloseIcon />}
          title='hold it'
        >
          <>
            <span className={styles.message}>
              You're currently viewing our {pageLocale} site.
            </span>
            <Link href={localizedEquivalent} locale={userLocale?.localeCode}>
              <a className={styles.link}>
                Switch to the {userLocale?.localeCode} site
              </a>
            </Link>
          </>
        </Alert>
      </div>
    );
  }

  return null;
};

export default ForeignContentNotification;
